import { useSyncExternalStore } from 'react';

/**
 * Tracks the app's own mobile breakpoint (max-width: 767px — the same one
 * Layout/AppShell.css, MobileBottomNav.css and MobileHeader.css switch chrome
 * on), so a component can pick a narrow-viewport treatment (e.g. CustomSelect
 * rendering a BottomSheet instead of its popover) with the same idea of
 * "mobile" the rest of the app already uses.
 *
 * Split out of BottomSheet.jsx: a file that exports a hook alongside a
 * component breaks Vite Fast Refresh (react-refresh/only-export-components).
 *
 * Built on useSyncExternalStore rather than a state+effect pair — the
 * idiomatic React API for subscribing to an external source (matchMedia is
 * exactly that), and it sidesteps the synchronous setState-in-effect an
 * initial-value-correction effect would otherwise need.
 *
 * jsdom doesn't implement `window.matchMedia` — every test in this repo that
 * needs it stubs it manually (see Layout/__tests__/Sidebar.test.jsx). Both
 * functions below default to "not narrow" rather than throwing when it's
 * missing, so a consumer that skips the stub keeps its pre-existing desktop
 * behaviour instead of crashing.
 */

const NARROW_QUERY = '(max-width: 767px)';

function hasMatchMedia() {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function subscribe(callback) {
    if (!hasMatchMedia()) return () => {};
    const mql = window.matchMedia(NARROW_QUERY);
    mql.addEventListener('change', callback);
    return () => mql.removeEventListener('change', callback);
}

function getSnapshot() {
    return hasMatchMedia() ? window.matchMedia(NARROW_QUERY).matches : false;
}

function getServerSnapshot() {
    return false;
}

export default function useIsNarrowViewport() {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
